import React from 'react'
import {
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material'
import { AvailabilityCheckStatus } from 'types/types'
import AvailabilityCheckResult from 'logic/AvailabilityCheckResult'
import { DateTime, Duration } from 'luxon'

type AvailabilityCheckCardProps = {
  check: AvailabilityCheckResult
  isPending: boolean
  updating: number | null
  onUpdateAvailabilityCheck: (checkId: number, status: AvailabilityCheckStatus) => void
}

const AvailabilityCheckCard: React.FC<AvailabilityCheckCardProps> = ({
  check,
  isPending,
  updating,
  onUpdateAvailabilityCheck,
}) => {
  const formatTime = (dateTime: DateTime, tz: string = 'Europe/London') => {
    return dateTime.setZone(tz).toFormat('h:mm a')
  }

  const formatDate = (dateTime: DateTime, tz: string = 'Europe/London') => {
    return dateTime.setZone(tz).toFormat('d MMM yyyy')
  }

  const formatDuration = (duration: Duration) => {
    return `${duration.as('minutes')} mins`
  }

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} key={check.id}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: isPending ? '2px solid' : '1px solid',
          borderColor: isPending ? 'error.main' : 'divider',
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              #{check.id}
            </Typography>
            <Chip
              label={check.status}
              size="small"
              color={isPending ? 'warning' : check.status === 'available' ? 'success' : 'error'}
            />
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Practice
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {check.availability_check_practice_name}
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Postcode
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {check.availability_check_postcode}
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Date
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {formatDate(check.availability_check_start_at)}
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Hours
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {formatTime(check.availability_check_start_at)} - {formatTime(check.availability_check_end_at)}
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Lunch Break
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {formatDuration(check.availability_check_lunch_break)}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Role
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {check.availability_check_role}
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {isPending ? 'Availability' : 'Your Response'}
            </Typography>
            {isPending ? (
              updating === check.id ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <RadioGroup
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  value={check.status}
                  onChange={(e) => onUpdateAvailabilityCheck(check.id, e.target.value as AvailabilityCheckStatus)}
                >
                  <FormControlLabel
                    value="available"
                    control={<Radio size="small" />}
                    label="Available"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                        color: check.status === 'available' ? 'success.main' : 'inherit'
                      }
                    }}
                  />
                  <FormControlLabel
                    value="unavailable"
                    control={<Radio size="small" />}
                    label="Unavailable"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                        color: check.status === 'unavailable' ? 'error.main' : 'inherit'
                      }
                    }}
                  />
                </RadioGroup>
              )
            ) : (
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 'medium',
                  color: check.status === 'available' ? 'success.main' : 'error.main'
                }}
              >
                {check.status === 'available' ? 'Available' : 'Unavailable'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  )
}

export default AvailabilityCheckCard 